import * as THREE from 'three'
import * as RAPIER from '@dimforge/rapier3d-compat'

import { GameObject } from './GameObject'
import { Camera } from '../components/Graphics/Camera'
import type { Engine } from '../core/Engine'
import { RapierDebugRenderer } from '../core/RapierDebugRenderer'
import { OutputPass, RenderPass } from 'three/examples/jsm/Addons.js'

export class Scene {
  engine: Engine

  //Graphics
  threeScene = new THREE.Scene()
  //Physics
  physicsWorld!: RAPIER.World
  physicsDebug!: RapierDebugRenderer
  //Movement
  safeMoveLoopLimit = 100

  private objects: GameObject[] = []
  private root: GameObject
  private destroyQueue: GameObject[] = []
  // a camera weak ref who can be undefined
  private mainCameraRef?: WeakRef<Camera>
  private _mainRenderPass?: RenderPass
  private _mainOutputPass?: OutputPass
  get mainCamera() { return this.mainCameraRef?.deref() }
  set mainCamera(camera: Camera | undefined) {
    //if changed
    if (this.mainCameraRef?.deref() !== camera) {
      console.log('main camera changed')
      if (this.engine.composer) {
        if (this._mainRenderPass) this.engine.composer.removePass(this._mainRenderPass)
        if (this._mainOutputPass) this.engine.composer.removePass(this._mainOutputPass)
        this._mainRenderPass = undefined
        this._mainOutputPass = undefined
        if (camera) {
          this._mainRenderPass = new RenderPass(this.engine.scene.threeScene, camera.graphicCamera)
          this.engine.composer.addPass(this._mainRenderPass)
          this._mainOutputPass = new OutputPass()
          this._mainOutputPass.renderToScreen = false
          this.engine.composer.addPass(this._mainOutputPass)
        }
      }
    }
    this.mainCameraRef = camera ? new WeakRef(camera) : undefined
  }
  constructor(engine: Engine) {
    this.engine = engine
    this.physicsWorld = new RAPIER.World({ x: 0, y: -9.81, z: 0 })
    this.physicsWorld!.timestep = this.engine.time.fixedDelta
    this.physicsDebug = new RapierDebugRenderer(this.threeScene, this.physicsWorld)

    this.root = new GameObject(this.engine, 'Root')
    this.add(this.root, null)
  }
  add(go: GameObject, parent?: GameObject | null) {
    go.scene = this
    if (parent !== null) {
      if (parent) go.parent = parent
      else go.parent = this.root
    }
    this.objects.push(go)
    go.start()
  }
  destroy() {
    for (const go of this.objects) {
      go.destroy()
    }
  }
  destroyGameObject(go: GameObject) {
    this.destroyQueue.push(go)
    for (const child of go.children) this.destroyGameObject(child)
  }
  fixedUpdate(dt: number) {
    this.physicsWorld.timestep = this.engine.time.fixedDelta
    for (const obj of this.objects) obj.fixedUpdate(dt)
    this.physicsWorld.step()
  }
  update(dt: number) {
    for (const obj of this.objects) obj.isMooving = obj.transform.isDirty()
    let sceneMoved = true
    let loopCount = 0
    for (const obj of this.objects) obj.update(dt)

    // resolve movements
    while (sceneMoved) {
      sceneMoved = false
      for (const obj of this.objects) {
        sceneMoved = obj.onMoveUpdate() || sceneMoved
      }
      loopCount++
      if (loopCount > this.safeMoveLoopLimit) {
        console.warn('Move loop limit reached')
        break
      }
    }
    for (const obj of this.objects) {
      obj.onAfterMoveUpdate()
    }

    // Destroy
    for (const go of this.destroyQueue) { go.onDestroy() }
    this.objects = this.objects.filter((go) => !this.destroyQueue.includes(go))
    this.destroyQueue = []
  }
  resize(width: number, height: number) {
    const mainCamera = this.mainCamera
    if (mainCamera) mainCamera.resize(width, height)
  }
  render(dt: number) {
    //set output pass at end
    //this.engine.composer?.removePass(this._mainOutputPass!)
    //this.engine.composer?.addPass(this._mainOutputPass!)
    for (const obj of this.objects) {
      obj.render(dt)
    }
    this.physicsDebug.render()

  }
}
